import {useRef, useState} from 'react';
import type {DeepPartial} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {financingEndingApi, type FinancingEndingPreviewResponseBody} from '@dms/api/sales';

import {type ApiError, type Nullish, useOnMount} from 'shared/web';

import type {FinancingEndingEditableEmailFormValues} from '../utils/FinancingEndingEditableEmailFormSchema';
import {
  type FinancingEndingEmailDraft,
  getFinancingEndingEmailDraft,
} from '../utils/getFinancingEndingEmailDraft';

const TEXT_EDIT_DEBOUNCE_MS = 1000;

export type FinancingEndingEditablePreviewInput = {
  rowId: string | Nullish;
  values: DeepPartial<FinancingEndingEditableEmailFormValues>;
};

type PreviewState =
  | {status: 'idle'}
  | {status: 'loading'; rowId: string}
  | {status: 'success'; rowId: string; preview: FinancingEndingPreviewResponseBody}
  | {status: 'error'; rowId: string; error: ApiError};

type PendingRequest = {abort: () => void};

type PreviewSnapshot = {
  version: number;
  rowId: string;
  draft: FinancingEndingEmailDraft;
};

const IDLE_STATE: PreviewState = {status: 'idle'};

/**
 * The per-recipient preview of the editable financing-ending e-mail.
 *
 * The caller reports each change from its event handler: `requestPreview` for a recipient,
 * mailbox or template change and on open, `schedulePreview` for a text edit (sent after the edits
 * pause), `resetPreview` on close. Every call supersedes the earlier ones: a waiting edit is
 * dropped, the request in flight is aborted, and a result or error that still arrives is ignored.
 * The returned state belongs to the latest call only. Values without a mailbox, subject or body,
 * or no recipient, clear the preview.
 *
 * The request text is the one the submit sends (see `getFinancingEndingEmailDraft`). The text is
 * sent in a mutation, so it never becomes a cache key. After the unmount no request starts; one
 * already in flight may finish.
 */
export function useFinancingEndingEditablePreview() {
  const [previewEmail] = financingEndingApi.usePreviewEditableFinancingEndingEmailMutation();
  const [state, setState] = useState<PreviewState>(IDLE_STATE);
  const versionRef = useRef(0);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRequestRef = useRef<PendingRequest | null>(null);
  // Child mount effects run before this hook's mount effect and may request the first preview.
  const isMountedRef = useRef(true);

  const cancelScheduled = () => {
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  const supersede = () => {
    versionRef.current += 1;
    cancelScheduled();
    pendingRequestRef.current?.abort();
    pendingRequestRef.current = null;

    return versionRef.current;
  };

  useOnMount(
    () => {
      isMountedRef.current = true;
    },
    () => {
      isMountedRef.current = false;
      cancelScheduled();
    }
  );

  const send = (snapshot: PreviewSnapshot) => {
    timeoutIdRef.current = null;

    if (!isMountedRef.current) {
      return;
    }

    const {version, rowId, draft} = snapshot;
    const isCurrent = () => versionRef.current === version;
    const request = previewEmail({
      financingEndingPreviewRequestBody: {
        rowId,
        emailCredentialsId: draft.emailCredentialsId,
        ...draft.source,
      },
    });
    pendingRequestRef.current = request;

    // Two handlers, not `.catch`: only a request error is a preview error, never a throw in the
    // success handler.
    request.unwrap().then(
      (preview) => {
        if (isCurrent()) {
          pendingRequestRef.current = null;
          setState({status: 'success', rowId, preview});
        }
      },
      (error: ApiError) => {
        if (isCurrent()) {
          pendingRequestRef.current = null;
          setState({status: 'error', rowId, error});
        }
      }
    );
  };

  const takeSnapshot = (input: FinancingEndingEditablePreviewInput): PreviewSnapshot | null => {
    const version = supersede();
    const draft = getFinancingEndingEmailDraft(input.values);

    if (isNil(input.rowId) || isNil(draft)) {
      setState(IDLE_STATE);
      return null;
    }

    setState({status: 'loading', rowId: input.rowId});

    return {version, rowId: input.rowId, draft};
  };

  const requestPreview = (input: FinancingEndingEditablePreviewInput) => {
    const snapshot = takeSnapshot(input);

    if (isNotNil(snapshot)) {
      send(snapshot);
    }
  };

  const schedulePreview = (input: FinancingEndingEditablePreviewInput) => {
    const snapshot = takeSnapshot(input);

    if (isNotNil(snapshot) && isMountedRef.current) {
      timeoutIdRef.current = setTimeout(() => send(snapshot), TEXT_EDIT_DEBOUNCE_MS);
    }
  };

  const resetPreview = () => {
    supersede();
    setState(IDLE_STATE);
  };

  return {
    rowId: state.status === 'idle' ? null : state.rowId,
    isLoading: state.status === 'loading',
    preview: state.status === 'success' ? state.preview : null,
    error: state.status === 'error' ? state.error : null,
    requestPreview,
    schedulePreview,
    resetPreview,
  };
}
